// src/modules/hrm/onLeave/OnLeaveDetailModal.jsx
import React, { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUmbrellaBeach,
  faXmark,
  faCalendarDays,
  faUser,
  faCheckCircle,
  faFile,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateVN, formatDateTime } from "../../../utils/formatUtils";

/** Phiếu có thể là nửa ngày (0,5): không dùng toFixed(0) vì làm tròn 0,5 → 1 */
function formatTotalLeaveDaysVi(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n =
    typeof value === "string" ? Number(String(value).replace(",", ".")) : Number(value);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n === 0) return "0 ngày";
  const rounded = Math.round(n * 10) / 10;
  const isWhole = Math.abs(rounded % 1) < 1e-9;
  const s = rounded.toLocaleString("vi-VN", {
    minimumFractionDigits: isWhole ? 0 : 1,
    maximumFractionDigits: 1,
  });
  return `${s} ngày`;
}

const OnLeaveDetailModal = ({ leave, onClose }) => {
  const isOpen = !!leave;

  // 🔹 ESC để đóng nhanh
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const statusMeta = useMemo(() => {
    const rawStatus = leave?.status || "";
    const normalized = rawStatus.toLowerCase();

    if (
      normalized.includes("đã duyệt") ||
      normalized.includes("da duyet") ||
      normalized === "approved"
    ) {
      return {
        label: "Đã duyệt",
        dot: "bg-emerald-500",
        badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
      };
    }

    if (
      normalized.includes("không duyệt") ||
      normalized.includes("khong duyet") ||
      normalized === "rejected"
    ) {
      return {
        label: "Không duyệt",
        dot: "bg-rose-500",
        badge: "bg-rose-50 text-rose-700 border-rose-100",
      };
    }

    return {
      label: rawStatus || "Chờ duyệt",
      dot: "bg-amber-400",
      badge: "bg-amber-50 text-amber-700 border-amber-100",
    };
  }, [leave]);

  return (
    <AnimatePresence>
      {isOpen && leave && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white w-full max-w-xl rounded-[2.2rem] shadow-2xl overflow-hidden border border-slate-100"
          >
            <div className="p-8 pb-0 flex items-start justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 shrink-0 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center border border-red-100 shadow-sm">
                  <FontAwesomeIcon icon={faUmbrellaBeach} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-black uppercase tracking-tight text-gray-800 truncate">
                    Chi tiết nghỉ phép
                  </h2>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5 italic">
                    Hệ thống xét duyệt phiếu IMA
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-2xl transition-all border border-gray-100"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-3xl border border-slate-100">
                <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center text-slate-600">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-black text-gray-800 uppercase tracking-tight truncate">
                    {leave.employee_name || "-"}
                  </p>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-1 truncate">
                    {(leave.branch_name || "-") + " • " + (leave.department_name || "-")}
                  </p>
                  <div
                    className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-[10px] font-black uppercase tracking-widest ${statusMeta.badge}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                    {statusMeta.label}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-orange-50 text-orange-600">
                      <FontAwesomeIcon icon={faCalendarDays} />
                    </div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Loại nghỉ
                    </span>
                  </div>
                  <span className="text-sm font-black text-gray-700">
                    {leave.leave_type || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                      <FontAwesomeIcon icon={faCalendarDays} />
                    </div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Thời gian nghỉ
                    </span>
                  </div>
                  <span className="text-sm font-black text-gray-700 text-right">
                    {formatDateVN(leave.from_date)} — {formatDateVN(leave.to_date)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
                      <FontAwesomeIcon icon={faCalendarDays} />
                    </div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Tổng số ngày
                    </span>
                  </div>
                  <span className="text-sm font-black text-gray-700 tabular-nums">
                    {formatTotalLeaveDaysVi(leave.total_days)}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                  Lý do nghỉ phép
                </h4>
                <div className="p-5 bg-amber-50/30 border border-amber-100/50 rounded-3xl">
                  <p className="text-sm text-amber-900 font-bold italic leading-relaxed">
                    "{leave.reason || "Không có ghi chú cụ thể"}"
                  </p>
                </div>
              </div>

              {leave.attached_file && (
                <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                  <a
                    href={leave.attached_file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 underline"
                  >
                    <FontAwesomeIcon icon={faFile} />
                    Xem file đính kèm
                  </a>
                </div>
              )}

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Thông tin phê duyệt
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-bold">Người duyệt</span>
                  <span className="text-gray-700 font-black">
                    {leave.approver_name || leave.approved_by_name || "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-bold">Thời gian duyệt</span>
                  <span className="text-gray-700 font-black inline-flex items-center gap-2">
                    <FontAwesomeIcon icon={faClockRotateLeft} className="text-gray-400" />
                    {formatDateTime(leave.approved_at)}
                  </span>
                </div>

                {(statusMeta.label === "Không duyệt" || leave.status === "Không duyệt") &&
                  leave.approval_note && (
                    <div className="mt-2 p-3 rounded-xl border border-rose-100 bg-rose-50 text-rose-700 text-sm font-bold">
                      <span className="inline-flex items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Lý do từ chối: {leave.approval_note}
                      </span>
                    </div>
                  )}
              </div>
            </div>

            <div className="px-8 pb-8 pt-0 flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all"
              >
                Đóng
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default OnLeaveDetailModal;
